import React, { useState, useEffect } from 'react';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from 'recharts';
import Select from 'react-select';
import { customStyle } from '@custom/Utils/utils';
import Header from '@custom/Header/Header';
import { getMonthlyPatientData, getPatientDataModalityWise } from '@custom/Apis/apis';
import { useLocation } from 'react-router-dom';
import { apiRequest } from '@custom/Utils/config/apiRequest';
import { _commonUrls } from '@custom/Utils/constants/apiUrls';
import { Loader } from '@ohif/ui';
import {useTheme} from "../../../ui/src/contextProviders"
import HighchartsReact from 'highcharts-react-official';
import Highcharts from 'highcharts';

const HospitalAnalytics = () => {
  const isDarkMode = useTheme();
  const location = useLocation();
  const hospitalId = location?.pathname.split("/")[3]

  const [ModalityOptions, setModalityOptions] = useState([]);
  const [YearsOptions, setYearsOptions] = useState([]);

  const [selectedModality, setSelectedModality] = useState(null);
  const [selectedYear, setSelectedYear] = useState(null);
  const [selectedYearModalityWise, setSelectedYearModalityWise] = useState(null);

  const [MonthlyPatientData, setMonthlyPatientData] = useState([]);
  const [ModalityWisePatientData, setModalityWisePatientData] = useState([]);

  const [Loading, setLoading] = useState(false);


  useEffect(() => {
    fetchCommonData();
    getYearsArray();
  }, [])

  useEffect(() => {
    getPatientDataMonthly();
  }, [selectedYear, hospitalId])

  useEffect(() => {
    getModalityWisePatientData()
  }, [selectedYearModalityWise, selectedModality])

  function getYearsArray(startYear = 2023) {
    const currentYear = new Date().getFullYear();
    const yearsArray = [];

    for (let year = currentYear; year >= startYear; year--) {
      yearsArray.push({ value: year.toString(), label: year.toString() });
    }
    setYearsOptions(yearsArray);
    setSelectedYear({ value: currentYear?.toString(), label: currentYear?.toString() });
    setSelectedYearModalityWise({ value: currentYear?.toString(), label: currentYear?.toString() });
  }

  const fetchCommonData = async () => {
    try {
      const commonData: any = await apiRequest({ method: 'POST', endUrl: _commonUrls.list, body: {}, headerType: 'json' })
      setModalityOptions(commonData?.result?.modality)
      setSelectedModality(commonData?.result?.modality[0])
    } catch (error) {
      console.error("Error fetching data:", error);
    }
  };


  const getPatientDataMonthly = async () => {
    try {
      if (hospitalId) {
        setLoading(true)
        const payload = {
          hospitalId: hospitalId,
          year: selectedYear?.value,
        }
        const response = await getMonthlyPatientData(payload)
        if (response?.status === "SUCCESS") {
          setMonthlyPatientData(response?.result)
        }
      }
    } catch (error) {
      console.log(error)
    } finally {
      setLoading(false)
    }
  }

  const getModalityWisePatientData = async () => {
    try {
      if (hospitalId) {
        setLoading(true)
        const payload = {
          hospitalId: hospitalId,
          year: selectedYearModalityWise?.value,
          modality: selectedModality?.value
        }
        const response = await getPatientDataModalityWise(payload)
        if (response?.status === "SUCCESS") {
          setModalityWisePatientData(response?.result)
        }
      }
    } catch (error) {
      console.log(error)
    } finally {
      setLoading(false)
    }
  }

  const monthlyChartOptions = {
    chart: {
      type: 'column',
      height: 300,
      backgroundColor: isDarkMode?.isDarkMode ? '#090c29' : '#ffffff',
    },
    title: {
      text: null,
    },
    xAxis: {
      categories: MonthlyPatientData.map(data => data.name),
      title: {
        text: null,
      },
      labels: {
        style: {
          color: isDarkMode?.isDarkMode ? '#ffffff' : '#000000',
        },
      },
      lineWidth: 0,
      tickWidth: 0,
    },
    yAxis: {
      title: {
        text: 'No of Patients',
        rotation: -90,
        margin: 15,
        style: {
          color: isDarkMode?.isDarkMode ? '#ffffff' : '#000000',
          textAnchor: 'middle',
        },
      },
      labels: {
        style: {
          color: isDarkMode?.isDarkMode ? '#ffffff' : '#000000',
        },
      },
      gridLineWidth: 0,
    },
    series: [
      {
        name: 'Patients',
        data: MonthlyPatientData.map(data => data.patients),
        color: '#007D7D',
        borderRadius: 8,
      },
    ],
    plotOptions: {
      column: {
        pointWidth: 26,
      },
    },
    tooltip: {
      enabled: true,
    },
    legend: {
      enabled: false,
    },
    credits: {
      enabled: false,
    },
  };

    const modalityChartOptions = {
    chart: {
      type: 'column',
      height: 300,
      backgroundColor: isDarkMode?.isDarkMode ? '#090c29' : '#ffffff',
    },
    title: {
      text: null,
    },
    xAxis: {
      categories: ModalityWisePatientData.map(data => data.name),
      title: {
        text: null,
      },
      labels: {
        style: {
          color: isDarkMode?.isDarkMode ? '#ffffff' : '#000000',
        },
      },
      lineWidth: 0,
      tickWidth: 0,
    },
    yAxis: {
      title: {
        text: 'No of Patients',
        rotation: -90,
        margin: 15,
        style: {
          color: isDarkMode?.isDarkMode ? '#ffffff' : '#000000',
          textAnchor: 'middle',
        },
      },
      labels: {
        style: {
          color: isDarkMode?.isDarkMode ? '#ffffff' : '#000000',
        },
      },
      gridLineWidth: 0,
    },
    series: [
      {
        name: 'Patients',
        data: ModalityWisePatientData.map(data => data.patients),
        color: '#007D7D',
        borderRadius: 8,
      },
    ],
    plotOptions: {
      column: {
        pointWidth: 26,
      },
    },
    tooltip: {
      enabled: true,
    },
    legend: {
      enabled: false,
    },
    credits: {
      enabled: false,
    },
  };

  // Define selectStyles as a function that takes isDarkMode as a parameter
const selectStyles = (isDarkMode) => ({
  control: (provided) => ({
    ...provided,
    backgroundColor: isDarkMode ? '#090c29' : '#ffffff',
    color: isDarkMode ? '#ffffff' : '#000000',
    borderColor: isDarkMode ? '#090c29' : '#cccccc',
    boxShadow: 'none',
    '&:hover': {
      borderColor: isDarkMode ? '#090c29' : '#aaaaaa',
    },
  }),
  singleValue: (provided) => ({
    ...provided,
    color: isDarkMode ? '#ffffff' : '#000000',
  }),
  menu: (provided) => ({
    ...provided,
    backgroundColor: isDarkMode ? '#090c29' : '#ffffff',
    zIndex: 9999, // Ensure dropdown is above other elements
  }),
  option: (provided, state) => ({
    ...provided,
    backgroundColor: state.isSelected
      ? isDarkMode ? '#1a1c3d' : '#e6e6e6'
      : isDarkMode ? '#090c29' : '#ffffff',
    color: isDarkMode ? '#ffffff' : '#000000',
    '&:hover': {
      backgroundColor: isDarkMode ? '#1a1c3d' : '#f5f5f5',
    },
  }),
  placeholder: (provided) => ({
    ...provided,
    color: isDarkMode ? '#cccccc' : '#888888',
  }),
  input: (provided) => ({
    ...provided,
    color: isDarkMode ? '#ffffff' : '#000000',
  }),
});

  return (
    <>
      <div className='page page__full' style={
      isDarkMode?.isDarkMode ? {background: '#090c29'} : customStyle.mainBackground
    }>
        <Header isBack={true} title={`${location?.state?.hospitalName}`}></Header>
        <div className='page__body'>
          <div className='details'>
            <div className="form__section">
              <div className="flex justify-between items-center mb-4">
                <h2 className="text-xl font-semibold">Volume of Patients</h2>
                <Select
                  options={YearsOptions}
                  value={selectedYear}
                  onChange={setSelectedYear}
                  className={`w-48`}
                  placeholder="Filter by Year"
                  styles={selectStyles(isDarkMode?.isDarkMode)}
                />
              </div>
              <div style={{ width: '100%', height: 300 }}>
                <HighchartsReact highcharts={Highcharts} options={monthlyChartOptions} />
              </div>
            </div>

            <div className="form__section">
              <div className="flex justify-between items-center mb-4">
                <h2 className="text-xl font-semibold">Modality Wise Patients</h2>
                <div className="flex space-x-4">
                  <Select
                    options={ModalityOptions}
                    value={selectedModality}
                    onChange={setSelectedModality}
                    className="w-48"
                    placeholder="Modality"
                    styles={selectStyles(isDarkMode?.isDarkMode)}
                  />
                  <Select
                    options={YearsOptions}
                    value={selectedYearModalityWise}
                    onChange={setSelectedYearModalityWise}
                    className="w-48"
                    placeholder="Filter by Year"
                    styles={selectStyles(isDarkMode?.isDarkMode)}
                  />
                </div>
              </div>
              <div style={{ width: '100%', height: 300 }}>
                <HighchartsReact highcharts={Highcharts} options={modalityChartOptions} />
              </div>
            </div>
          </div>

        </div>
      </div>
      <Loader isLoaderOpen={Loading} />
    </>

  );
};

export default HospitalAnalytics;
